import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { DOC_TEMPLATES, templateCategories, getTemplateBySlug, type DocTemplate } from "@/lib/doc-templates";
import { generateDocument } from "@/lib/generator.functions";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, ArrowRight, ArrowLeft, FileText } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";

export const Route = createFileRoute("/_authenticated/app/generator")({
  component: GeneratorPage,
});

type Matter = { id: string; name: string };

function GeneratorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [tplSlug, setTplSlug] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, any>>({});
  const [tone, setTone] = useState("Formale");
  const [matterId, setMatterId] = useState<string>("");
  const [attachContext, setAttachContext] = useState(false);
  const [matters, setMatters] = useState<Matter[]>([]);
  const [loading, setLoading] = useState(false);
  const genFn = useServerFn(generateDocument);

  useEffect(() => {
    supabase.from("matters").select("id, name").order("updated_at", { ascending: false }).limit(50)
      .then(({ data }) => setMatters((data as any) ?? []));
  }, []);

  const tpl = tplSlug ? getTemplateBySlug(tplSlug) : null;

  const choose = (t: DocTemplate) => {
    setTplSlug(t.slug);
    setTone(t.tones[0]);
    setFields({});
    setStep(2);
  };

  const generate = async () => {
    if (!tpl) return;
    const missing = tpl.fields.filter((f) => f.required && !String(fields[f.name] ?? "").trim());
    if (missing.length) return toast.error(t("pages.generator.missingFields", { fields: missing.map((m) => m.label).join(", ") }));
    setLoading(true);
    try {
      const r = await genFn({
        data: {
          slug: tpl.slug,
          fields,
          tone,
          matterId: matterId || null,
          attachContext: attachContext && !!matterId,
        },
      });
      if (!r.ok) return toast.error(r.error);
      toast.success(t("pages.generator.generated"));
      navigate({ to: "/app/drafting" });
    } finally { setLoading(false); }
  };

  return (
    <>
      <PageHeader title={t("pages.generator.title")} description={t("pages.generator.description")} />
      <PageBody>
        {/* Stepper */}
        <div className="mb-6 flex items-center gap-2 text-xs">
          {[
            { n: 1, label: t("pages.generator.stepType") },
            { n: 2, label: t("pages.generator.stepData") },
            { n: 3, label: t("pages.generator.stepTone") },
          ].map((s, i) => (
            <div key={s.n} className="flex items-center gap-2">
              <div className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${step >= s.n ? "bg-foreground text-background" : "bg-muted text-muted-foreground"}`}>{s.n}</div>
              <span className={step === s.n ? "font-medium" : "text-muted-foreground"}>{s.label}</span>
              {i < 2 && <div className="mx-1 h-px w-8 bg-border" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-6">
            {templateCategories().map((cat) => (
              <div key={cat}>
                <h3 className="mb-3 text-sm font-semibold text-muted-foreground">{cat}</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {DOC_TEMPLATES.filter((t) => t.category === cat).map((t) => (
                    <Card key={t.slug} className="cursor-pointer p-4 transition-shadow hover:shadow-md" onClick={() => choose(t)}>
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-foreground text-background">
                          <FileText className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium leading-tight">{t.label}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{t.description}</div>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {t.tones.slice(0, 2).map((tn) => <Badge key={tn} variant="secondary" className="text-[10px]">{tn}</Badge>)}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {step === 2 && tpl && (
          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h3 className="font-display text-xl font-semibold">{tpl.label}</h3>
                <p className="text-sm text-muted-foreground">{tpl.description}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setStep(1)}><ArrowLeft className="mr-1 h-3 w-3" /> {t("pages.generator.backToType")}</Button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {tpl.fields.map((f) => (
                <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                  <Label>{f.label}{f.required && <span className="text-destructive"> *</span>}</Label>
                  {f.type === "textarea" ? (
                    <Textarea rows={3} value={fields[f.name] ?? ""} onChange={(e) => setFields({ ...fields, [f.name]: e.target.value })} placeholder={f.placeholder} />
                  ) : f.type === "select" ? (
                    <select value={fields[f.name] ?? ""} onChange={(e) => setFields({ ...fields, [f.name]: e.target.value })} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                      <option value="">-</option>
                      {f.options?.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={fields[f.name] ?? ""} onChange={(e) => setFields({ ...fields, [f.name]: e.target.value })} placeholder={f.placeholder} />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>{t("pages.generator.back")}</Button>
              <Button onClick={() => setStep(3)}>{t("pages.generator.continue")} <ArrowRight className="ml-1 h-3 w-3" /></Button>
            </div>
          </Card>
        )}

        {step === 3 && tpl && (
          <Card className="p-6">
            <h3 className="font-display text-xl font-semibold">{t("pages.generator.toneCtxTitle")}</h3>
            <p className="text-sm text-muted-foreground">{t("pages.generator.toneCtxDesc")}</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label>{t("pages.generator.tone")}</Label>
                <select value={tone} onChange={(e) => setTone(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  {tpl.tones.map((tn) => <option key={tn} value={tn}>{tn}</option>)}
                </select>
              </div>
              <div>
                <Label>{t("pages.generator.linkedMatter")}</Label>
                <select value={matterId} onChange={(e) => setMatterId(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                  <option value="">{t("pages.generator.noneOption")}</option>
                  {matters.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            </div>
            {matterId && (
              <label className="mt-4 flex items-start gap-2 text-sm">
                <input type="checkbox" checked={attachContext} onChange={(e) => setAttachContext(e.target.checked)} className="mt-1" />
                <span>
                  <span className="font-medium">{t("pages.generator.attachContext")}</span>
                  <span className="block text-xs text-muted-foreground">{t("pages.generator.attachContextDesc")}</span>
                </span>
              </label>
            )}
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep(2)}>{t("pages.generator.back")}</Button>
              <Button onClick={generate} disabled={loading} className="gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {t("pages.generator.generateDraft")}
              </Button>
            </div>
          </Card>
        )}
      </PageBody>
    </>
  );
}
